import React, { useState } from "react";
import { FormikProvider, useFormik } from "formik";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { addProduct } from "../../../../redux/Product/action";
import { addProductValidation } from "../../../Constant/constant";
import { usePermissions } from "../../../../Hooks/usePermissions";
import { MENU, PERMISSION } from "../../../../lib/permissions";
import ProductFormShell from "./form/ProductFormShell";
import { FormOptionsContext } from "./form/FormOptionsContext";
import { useProductFormOptions } from "./form/useProductFormOptions";
import { DEFAULT_PRODUCT_VALUES } from "./form/values";
import { BarcodeFieldError, normalizeBarcode } from "./form/barcode";
import { stripUnpermittedPriceFields } from "./form/payload";
import "./Product.css";

const BASE_PATH = "/addproduct";

const AddProduct = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { hasAction } = usePermissions();
  const canEditCost = hasAction(MENU.PRODUCT_COSTS, PERMISSION.EDIT);
  const canEditRegularPrice = hasAction(
    MENU.PRODUCT_REGULAR_PRICE,
    PERMISSION.EDIT,
  );
  const canEditSalePrice = hasAction(MENU.PRODUCT_SALE_PRICE, PERMISSION.EDIT);

  const formik = useFormik({
    initialValues: DEFAULT_PRODUCT_VALUES,
    validationSchema: addProductValidation({
      canSeeCost: canEditCost,
      canSeeRegularPrice: canEditRegularPrice,
      canSeeSalePrice: canEditSalePrice,
    }),
    onSubmit: async (values, { setFieldError, setFieldTouched }) => {
      const base = stripUnpermittedPriceFields(values, {
        canEditCost,
        canEditRegularPrice,
        canEditSalePrice,
      });
      const payload = { ...base, barcode: normalizeBarcode(values.barcode) };
      try {
        await dispatch(addProduct(payload, setLoading, navigate));
      } catch (err) {
        if (err instanceof BarcodeFieldError) {
          setFieldError("barcode", err.fieldMessage);
          setFieldTouched("barcode", true, false);
          return;
        }
        // swal already shown by addProduct; swallow so Formik resets isSubmitting
      }
    },
  });

  const options = useProductFormOptions({
    selectedCategoryIds: formik.values.category_ids,
    selectedSubCategoryIds: formik.values.sub_category_ids,
  });

  return (
    <FormikProvider value={formik}>
      <FormOptionsContext.Provider value={options}>
        <ProductFormShell
          mode="create"
          title="Add Product"
          basePath={BASE_PATH}
          loading={loading}
          onImportImages={() => navigate("/product/import-images")}
          // Without cost permission, the dispatcher will always stage this for
          // manager review — surface that in the button copy so the admin
          // knows what clicking does before they click it.
          submitLabel={canEditCost ? "Publish Product" : "Save to Pending"}
        />
      </FormOptionsContext.Provider>
    </FormikProvider>
  );
};

export default AddProduct;
